import React, { useState } from 'react';
import { FileText, Download, Youtube, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function AttachmentDisplay({ attachments, testIdPrefix = 'attach-display' }) {
  const [lightbox, setLightbox] = useState(null);
  if (!attachments || attachments.length === 0) return null;

  const images = attachments.filter(a => a.kind === 'image');
  const videos = attachments.filter(a => a.kind === 'youtube');
  const files = attachments.filter(a => a.kind === 'file');

  return (
    <div className="mt-4 space-y-4">
      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid={`${testIdPrefix}-images`}>
          {images.map((a, i) => {
            const src = a.url.startsWith('http') ? a.url : `${API}/files/${a.url}`;
            return (
              <button key={i} type="button" onClick={() => setLightbox(src)}
                className="relative aspect-square overflow-hidden rounded-lg border border-slate-200 bg-slate-50 hover:ring-2 hover:ring-emerald-500 transition"
                data-testid={`${testIdPrefix}-image-${i}`}>
                <img src={src} alt={a.filename} className="w-full h-full object-cover" />
              </button>
            );
          })}
        </div>
      )}

      {videos.map((a, i) => (
        <div key={i} className="aspect-video w-full max-w-2xl rounded-xl overflow-hidden bg-black border border-slate-200" data-testid={`${testIdPrefix}-youtube-${i}`}>
          <iframe
            src={a.url}
            title={a.filename || 'YouTube video'}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
            frameBorder="0"
          />
        </div>
      ))}

      {files.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid={`${testIdPrefix}-files`}>
          {files.map((a, i) => {
            const href = a.url.startsWith('http') ? a.url : `${API}/files/${a.url}`;
            return (
              <a key={i} href={href} target="_blank" rel="noreferrer" download={a.filename}
                className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 hover:border-emerald-300 hover:text-emerald-700 transition"
                data-testid={`${testIdPrefix}-file-${i}`}>
                <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                <span className="truncate max-w-[200px]">{a.filename || 'dosya'}</span>
                <span className="text-xs text-slate-400">{formatSize(a.size)}</span>
                <Download className="w-3.5 h-3.5 text-slate-400 ml-1" />
              </a>
            );
          })}
        </div>
      )}

      {lightbox && (
        <div
          className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setLightbox(null)}
          data-testid={`${testIdPrefix}-lightbox`}
        >
          <button className="absolute top-4 right-4 text-white/80 hover:text-white" onClick={() => setLightbox(null)}>
            <X className="w-6 h-6" />
          </button>
          <img src={lightbox} alt="" className="max-h-full max-w-full object-contain rounded" />
        </div>
      )}
    </div>
  );
}
